<template>
	<!-- Spinner while downloading -->
	<loader-generic v-if="showOverlaySpinner" isOverlay/>
	<!-- Toast for Download feedback -->
	<toast-generic v-if="showToast" :feedbackText="toastText" :toastType="toastType" @hideMe="showToast = false"/>
</template>

<script setup>
import LoaderGeneric from '@/components/generic/loaders/Generic'
import ToastGeneric from '@/components/generic/toasts/Generic'
/* Vue & Assets */
import { ref, watch } from 'vue'
import { mobileClientDownloadValidator } from '@/network/clients/mobileClientSetup'
import { fileDownloader } from '@/assets/scripts/fileDownloader'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['clearDownloadLink', 'clearDownloadLink'])

/* Props
@downloadLink {String} - link to download
@customSuccessMessage {String} - custom message for success toast
@fileName {String} hardcoded name for file
@fileType {String} prop to apply fileType do name in case headers doesnt have a content-disposition
*/
const props = defineProps({
    downloadLink: String,
	customSuccessMessage: String,
	fileName: String,
    fileType: {
        type: String,
        default: 'pdf'
    }
})

const showOverlaySpinner = ref(false)
const showToast = ref(false)
const toastText = ref('')
const toastType = ref('')

/* Triggering Download when downloadLink is populated */
watch(() => props.downloadLink, (newVal, oldVal) => {
	if (props.downloadLink) downloadHandler()
})

/* We need to check which platform we are in, for different download behaviours */
const downloadHandler = () => {
	if (window.cordova) {
		downloadHandlerMobileApp()
	} else {
		downloadHandlerBrowser()
	}
}

/*************************************************
*												 *
*                     BROWSER                    *
*												 *
*************************************************/
const downloadHandlerBrowser = async () => {
	showOverlaySpinner.value = true
	try {
		await fileDownloader(props.downloadLink, props.fileName)
		downloaderSuccessHandler()
	} catch (error) {
		if (openFileNewTab(error)) return
		downloaderErrorHandler(error.message)
	}
}

const openFileNewTab = error => {
	if (error.message !== 'Network Error') return false
	showOverlaySpinner.value = false
	window.open(props.downloadLink, '_blank')
	emit('clearDownloadLink')
	return true
}

/*************************************************
*												 *
*                 ANDROID & iOS                  *
*												 *
*************************************************/
/* Both mobile platforms download through the authenticated Advanced-HTTP plugin
   (the same trusted SSL/trust stack the rest of the app uses) and then open the
   file with fileOpener2. The OS system DownloadManager is intentionally NOT used
   on Android: it makes its own request with the device default trust store
   (fails on private-CA endpoints) and reports success on enqueue, producing a
   false-success toast even when nothing was written (issue #116148). */
const downloadHandlerMobileApp = async () => {
	showOverlaySpinner.value = true

	try {
		const res = await mobileClientDownloadValidator({ method: 'GET', url: props.downloadLink })

		let fileName
		if (props.fileName) fileName = props.fileName
		else if (res.headers['content-disposition']) {
			const splitVariable = res.headers['content-disposition'].includes('fileName') ? 'fileName=' : 'filename='
			fileName = res.headers['content-disposition'].split(splitVariable)[1]
		} else fileName = `download.${props.fileType}`

		/* Strip characters that are illegal in a file:// URI / Android file path.
		   Advanced-HTTP's downloadFile parses filePath as a java.net.URI and throws
		   "Illegal character in path" on spaces and * ? : | " < > \ / @ — MDW receipt
		   names contain them, e.g. "Comprovativo de Movimento …@@MM*0000112.pdf" (#116148). */
		fileName = fileName.replaceAll('"', '').trim().replace(/[\\/:*?<>|@\s]+/g, '_')
		/* iOS Cordova HTTP plugin returns Content-Disposition bytes as Latin-1.
		   Re-interpret them as UTF-8 to restore accented characters (ã, é, etc.).
		   Android returns them correctly, so only decode on iOS. */
		if (window.cordova.platformId === 'ios') {
			try { fileName = decodeURIComponent(escape(fileName)) } catch (e) { /* keep original if already valid */ }
		}

		/* iOS: app-private data dir. Android: app-specific external storage — needs no
		   runtime permission on modern Android and is openable via fileOpener2's FileProvider. */
		const storageDir = window.cordova.platformId === 'ios' ? window.cordova.file.dataDirectory : window.cordova.file.externalDataDirectory
		const filePath = storageDir + fileName
		mobileDownloadingFile(filePath)
	} catch (error) {
		downloaderErrorHandler(error.response ? error.response.data.message : null)
	}
}

const mobileDownloadingFile = async filePath => {
	try {
		await mobileClientDownloadValidator({ method: 'downloadFile', url: props.downloadLink, filePath: filePath })
		downloaderSuccessHandler()
		let fileExtension = 'application/pdf'

		if (filePath.includes('.xlsx')) fileExtension = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
		else if (filePath.includes('.xls')) fileExtension = 'application/vnd.ms-excel'

		window.cordova.plugins.fileOpener2.open(
			filePath,
			fileExtension,
			{
				error: (e) => {
					console.log('Error status: ' + e.status + ' - Error message: ' + e.message)
				},
				success: () => {
					console.log('file opened successfully')
				}
			}
		)
	} catch (error) {
		downloaderErrorHandler(error.response ? error.response.data.message : null)
	}
}

/*************************************************
*             Success & Error Handlers           *
*************************************************/
/* Download Success Handler */
const downloaderSuccessHandler = () => {
	showOverlaySpinner.value = false
	showToast.value = true
	toastType.value = 'success'
	toastText.value = props.customSuccessMessage || t('genericDownloadReceiptSuccessMessage')
	emit('clearDownloadLink')
}
/* Download Error Handler */
const downloaderErrorHandler = errorMessage => {
	showOverlaySpinner.value = false
	showToast.value = true
	toastType.value = 'error'
	toastText.value = errorMessage
	emit('clearDownloadLink')
}
</script>

<style lang="scss">
</style>
